import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da0s9jg3256a3o8kp08g";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("company_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      // Bot detected — show fake success
      setStatus("success");
      form.reset();
      return;
    }

    // Remove honeypot from payload
    formData.delete("company_alt");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        body: formData,
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const serverMsg =
        (parsed as Record<string, Record<string, string>>)?.meta?.message ||
        (parsed as Record<string, string>)?.message ||
        (parsed as Record<string, Record<string, string>>)?.meta?.detail ||
        responseText;

      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.toLowerCase().includes("spam") ||
          serverMsg.toLowerCase().includes("form data is spam"));

      if (response.ok && !isSpam && (parsed?.code === "OK" || !parsed?.code)) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "Submission failed. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again later.");
    }
  };

  return (
    <section
      id="contact"
      className="w-full bg-background-50 py-10 md:py-12 lg:py-14"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary-600 mb-4">
            Get in Touch
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
            Contact
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12">
          {/* Contact Info */}
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">
                Amin &amp; Jahan Corporation Ltd.
              </h3>
              <address className="not-italic text-base text-foreground-700 leading-relaxed">
                Road: 3, House: 47, 2nd Floor
                <br />
                Sector 13, Uttara
                <br />
                Dhaka 1230, Bangladesh
              </address>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground-800 uppercase tracking-wide mb-3">
                Online
              </h4>
              <ul className="flex flex-col gap-2">
                <li>
                  <a
                    href="https://aminjahan.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-2"
                  >
                    <i className="ri-global-line" />
                    aminjahan.com
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@aminjahan.com"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-2"
                  >
                    <i className="ri-mail-line" />
                    info@aminjahan.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 md:p-8">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-6">
              Send a Message
            </h3>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-5"
            >
              {/* Honeypot */}
              <div className="hp-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  defaultValue=""
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="contact-name"
                    className="text-xs font-medium text-foreground-700"
                  >
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 text-foreground-900"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="contact-email"
                    className="text-xs font-medium text-foreground-700"
                  >
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 text-foreground-900"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-subject"
                  className="text-xs font-medium text-foreground-700"
                >
                  Subject
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  required
                  className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 text-foreground-900"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="contact-message"
                  className="text-xs font-medium text-foreground-700"
                >
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3.5 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 text-foreground-900 resize-none"
                />
                <p className="text-xs text-foreground-500">
                  Maximum 500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="px-6 py-3 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap self-start"
              >
                {status === "submitting" ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3">
                  Thank you. Your message has been sent successfully.
                </p>
              )}

              {status === "error" && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                  {errorMsg}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}